import { AxiosInstance } from 'axios';
import { MutableRefObject, useEffect } from 'react';
import { MicroPortalMessageType } from '../components';

interface TokenResponse {
  sessionTransferToken: string;
}

export const createMicroPortalRoute = (app: string, params: string): string => {
  return `/micro-portal/authenticate/${app}?${params}`;
};

export const createMicroPortalUrl = (app: string, params: string, customHost?: string): string => {
  return `${customHost || ''}/extension/${app}?${params}`;
};

export const getMicroPortalToken = async (axios: AxiosInstance, app: string): Promise<string | undefined> => {
  try {
    const res = await axios.post<TokenResponse>(`/api/v1/auth/micro-portal/${app}/init`);
    if (res.data.sessionTransferToken) {
      return res.data.sessionTransferToken;
    }
    throw new Error('No session transfer token received');
  } catch (error) {
    console.error('Error fetching micro portal token:', error);
    throw error;
  }
};

export const sendMicroPortalRefreshMessage = (iframe: HTMLIFrameElement): void => {
  try {
    iframe.contentWindow?.postMessage({ type: MicroPortalMessageType.REFRESH });
  }
  catch (error) {
    console.error(error);
  }
};

export const useMicroPortalCloseListener = (iframeRef: MutableRefObject<HTMLIFrameElement | undefined>, onClose: VoidFunction): void => {
  useEffect(() => {
    const onMessage = (event: MessageEvent<{ type?: string }>): void => {
      try {
        if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
          return;
        }
        if (!event.data.type) {
          return;
        }
        if (event.data.type === MicroPortalMessageType.CLOSE) {
          onClose();
        }
      }
      catch (error) {
        console.error(error);
      }
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [onClose, iframeRef]);
};

export const useMicroPortalResizeListener = (
  iframeRef: MutableRefObject<HTMLIFrameElement | undefined>,
  onResize: (height: number) => void,
): void => {
  useEffect(() => {
    const onMessage = (event: MessageEvent<{
      type?: string;
      height?: number;
      value?: number;
      payload?: { height?: number };
    }>): void => {
      try {
        if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
          return;
        }
        if (event.data.type !== MicroPortalMessageType.RESIZE) {
          return;
        }

        const nextHeight = event.data.height ?? event.data.payload?.height ?? event.data.value;
        if (typeof nextHeight !== 'number' || !Number.isFinite(nextHeight) || nextHeight <= 0) {
          return;
        }

        onResize(nextHeight);
      }
      catch (error) {
        console.error(error);
      }
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [iframeRef, onResize]);
};
